import { faChevronDown, faChevronRight } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/design-system/primitives/Button';
import { Modal } from '@/design-system/primitives/Modal';
import type { CategoryGroup } from '@/hooks/useCategoryGroups';
import type { EnabledSubcategoriesApi } from '@/hooks/useEnabledSubcategories';

export interface CategoryCustomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  groups: Array<CategoryGroup> | null;
  isLoading: boolean;
  error: string | null;
  selection: EnabledSubcategoriesApi;
}

type GroupState = 'all' | 'none' | 'partial';

const getGroupState = (group: CategoryGroup, enabled: Set<string>): GroupState => {
  let on = 0;
  for (const c of group.categories) if (enabled.has(c.id)) on++;
  if (on === 0) return 'none';
  if (on === group.categories.length) return 'all';
  return 'partial';
};

interface GroupRowProps {
  group: CategoryGroup;
  state: GroupState;
  expanded: boolean;
  enabled: Set<string>;
  onToggleExpanded: () => void;
  onToggleGroup: (on: boolean) => void;
  onToggleSubcategory: (id: string) => void;
}

const GroupRow = ({
  group,
  state,
  expanded,
  enabled,
  onToggleExpanded,
  onToggleGroup,
  onToggleSubcategory,
}: GroupRowProps) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  // Reflect the tri-state via the DOM since `indeterminate` isn't a React prop.
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = state === 'partial';
  }, [state]);

  return (
    <div className="border-b border-content-divider last:border-b-0">
      <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-content-focal/40 transition-colors">
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={state === 'all'}
          onChange={(e) => onToggleGroup(e.target.checked)}
          className="w-4 h-4 cursor-pointer accent-secondary-emphasized"
          aria-label={`Toggle all ${group.label}`}
        />
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex-1 flex items-center gap-2 text-left cursor-pointer"
          aria-expanded={expanded}
        >
          <FontAwesomeIcon
            icon={expanded ? faChevronDown : faChevronRight}
            className="text-content-subtle"
            style={{ width: 10, height: 10 }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-content-text">{group.label}</div>
            {group.description && (
              <div className="text-xs text-content-subtle truncate">{group.description}</div>
            )}
          </div>
          <div className="text-xs text-content-subtle shrink-0">
            {state === 'all'
              ? `${group.categories.length}/${group.categories.length}`
              : state === 'none'
                ? `0/${group.categories.length}`
                : `${[...group.categories].filter((c) => enabled.has(c.id)).length}/${group.categories.length}`}
          </div>
        </button>
      </div>

      {expanded && (
        <div className="pl-9 pr-3 pb-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {group.categories.map((c) => (
            <label
              key={c.id}
              className="flex items-center gap-2 py-1 cursor-pointer text-sm text-content-text hover:text-content-emphasized transition-colors"
            >
              <input
                type="checkbox"
                checked={enabled.has(c.id)}
                onChange={() => onToggleSubcategory(c.id)}
                className="w-3.5 h-3.5 cursor-pointer accent-secondary-emphasized shrink-0"
              />
              <span className="truncate">{c.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export const CategoryCustomizationModal = ({
  isOpen,
  onClose,
  groups,
  isLoading,
  error,
  selection,
}: CategoryCustomizationModalProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Pending edits — all toggles mutate this local Set, not the shared
  // selection. We commit pending → selection only on close, so AppInner (and
  // the place search) doesn't churn on every checkbox click.
  const [pending, setPending] = useState<Set<string>>(new Set());

  // On open: snapshot the current committed selection into pending. Also
  // reset the expansion UI.
  useEffect(() => {
    if (isOpen) {
      setExpandedGroups(new Set());
      setPending(new Set(selection.enabled));
    }
  }, [isOpen, selection.enabled]);

  // Close commits the pending Set back to the real selection.
  const commitAndClose = useCallback(() => {
    selection.setEnabled(pending);
    onClose();
  }, [pending, selection, onClose]);

  const totalEnabled = pending.size;
  const totalAvailable = useMemo(
    () => (groups ?? []).reduce((sum, g) => sum + g.categories.length, 0),
    [groups],
  );

  const toggleExpanded = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSubcategoryLocal = (id: string) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroupLocal = (group: CategoryGroup, on: boolean) => {
    setPending((prev) => {
      const next = new Set(prev);
      for (const c of group.categories) {
        if (on) next.add(c.id);
        else next.delete(c.id);
      }
      return next;
    });
  };

  const resetLocal = () => {
    if (!groups) return;
    const defaults = new Set<string>();
    for (const g of groups) {
      if (g.enabledByDefault) for (const c of g.categories) defaults.add(c.id);
    }
    setPending(defaults);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={commitAndClose}
      size="lg"
      className="max-h-[85vh] flex flex-col"
      title={
        <div className="pr-10">
          <h2 className="font-diary text-2xl text-content-text">Customize categories</h2>
          <p className="text-xs text-content-subtle mt-1">
            Each enabled subcategory makes one extra Foursquare call per uncached search. Changes
            apply when you close this dialog.
          </p>
        </div>
      }
    >
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="p-8 text-center text-sm text-content-subtle">Loading categories…</div>
        )}
        {error && (
          <div className="p-8 text-center text-sm text-danger-text">Failed to load: {error}</div>
        )}
        {groups && !isLoading && !error && (
          <div>
            {groups.map((group) => (
              <GroupRow
                key={group.id}
                group={group}
                state={getGroupState(group, pending)}
                expanded={expandedGroups.has(group.id)}
                enabled={pending}
                onToggleExpanded={() => toggleExpanded(group.id)}
                onToggleGroup={(on) => toggleGroupLocal(group, on)}
                onToggleSubcategory={toggleSubcategoryLocal}
              />
            ))}
          </div>
        )}
      </div>
      <Modal.Footer className="justify-between">
        <div className="text-xs text-content-subtle">
          {totalEnabled} of {totalAvailable} subcategories enabled
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={resetLocal}>
            Reset to defaults
          </Button>
          <Button size="sm" className="rounded-full" onClick={commitAndClose}>
            Done
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
